// A linha da lista de clientes e as células dela. (#880)

import { type CSSProperties, type ReactNode } from 'react';
import { DIAS_PARA_ESFRIAR, type ClienteDaLista } from '@cliqcrm/contracts';
import { idadeEmPalavras, instantePorExtenso } from '@/shared/ui/estados/idade';
import { dinheiro } from '@/shared/lib/formato';
import { corDaEtapa, nomeDaEtapa } from '@/entities/comercial/etapa';
import { Etiquetas } from '@/entities/atendimento/etiqueta';
import { nomeDoCanal } from '@/entities/atendimento/canal';
import { Avatar, Icone, ListaLinha } from '@/shared/ui';
import { type AcoesDaLinha } from './acoes-da-linha';
import { Controles } from './controles';

/** Quando foi o último contato, e se isso é motivo de atenção. */
function UltimoContato({ cliente }: { cliente: ClienteDaLista }): ReactNode {
  /*
   * Âmbar SÓ quando há negócio aberto.
   *
   * `--atencao` quer dizer cuidado, e é isso mesmo: trinta dias sem contato
   * com proposta em aberto é dinheiro saindo pela porta. Sem negócio aberto,
   * trinta dias é normal — e pintar a lista toda ensinaria a ignorar a cor.
   *
   * O número vem do contrato, e não daqui: é o mesmo que a API usa para montar
   * o recorte "Esfriando". Duas constantes divergiriam, e a lista mostraria em
   * âmbar quem o filtro não traz.
   */
  const dias = cliente.ultima_interacao_em
    ? (Date.now() - Date.parse(cliente.ultima_interacao_em)) / 86_400_000
    : Number.POSITIVE_INFINITY;
  const frio = cliente.oportunidades_abertas > 0 && dias >= DIAS_PARA_ESFRIAR;

  return (
    <span
      className={frio ? 'clientes__quando clientes__quando--frio' : 'clientes__quando'}
      title={instantePorExtenso(cliente.ultima_interacao_em) ?? undefined}
    >
      {/*
        "Nunca conversou" é escrito, e não omitido: a ausência de data numa
        coluna de datas parece falha de carregamento, e é justamente o cliente
        que ninguém atendeu — o que mais merece atenção numa lista de vendas.
      */}
      {cliente.ultima_interacao_em
        ? idadeEmPalavras(cliente.ultima_interacao_em)
        : 'nunca conversou'}
    </span>
  );
}

/**
 * Na tabela, o "quando" e, embaixo, por onde: "há 2 horas · WhatsApp". O canal
 * é o da conversa mais recente; sem conversa, a segunda linha não existe.
 */
function UltimoContatoComCanal({ cliente }: { cliente: ClienteDaLista }): ReactNode {
  const canal = nomeDoCanal(cliente.ultima_interacao_canal);
  return (
    <span className="clientes__duas-linhas">
      <UltimoContato cliente={cliente} />
      {canal && <span className="clientes__apoio">{canal}</span>}
    </span>
  );
}

/**
 * A etapa do maior negócio aberto, num chip com a cor que a etapa tem no funil
 * (Configurações → Etapas do funil): a mesma etapa tem a mesma cor em toda
 * linha, e o olho agrupa o funil de relance. Sem negócio aberto, "Sem etapa",
 * neutro.
 */
function EtapaDoNegocio({ cliente }: { cliente: ClienteDaLista }): ReactNode {
  const negocio = cliente.negocio;
  if (!negocio?.etapa) {
    return (
      <span className="clientes__etapa clientes__etapa--sem">
        <Icone nome="funil" />
        <span className="clientes__etapa-nome">Sem etapa</span>
      </span>
    );
  }
  const cor = corDaEtapa(negocio.etapa_cor);
  const nome = nomeDaEtapa(negocio.etapa_nome ?? negocio.etapa);
  return (
    <span
      className="clientes__etapa"
      {...(cor ? { style: { '--cor-da-etapa': cor } as CSSProperties } : {})}
      title={
        cliente.oportunidades_abertas > 1
          ? `${nome} — a etapa da maior de ${cliente.oportunidades_abertas} oportunidades abertas`
          : nome
      }
    >
      <Icone nome="funil" />
      <span className="clientes__etapa-nome">{nome}</span>
    </span>
  );
}

/**
 * Como falar com ele: o e-mail e o telefone, um por linha, cada um com o ícone.
 * Os dois vêm mascarados do servidor, e a tela não tenta completar: quem
 * precisa do número abre a ficha, onde a leitura fica registrada.
 */
function Contato({ cliente }: { cliente: ClienteDaLista }): ReactNode {
  if (!cliente.email && !cliente.telefone) {
    return <span className="clientes__nada">sem contato</span>;
  }
  return (
    <span className="clientes__duas-linhas clientes__meios">
      {cliente.email && (
        <span className="clientes__meio">
          <Icone nome="email" />
          <span className="clientes__meio-texto">{cliente.email}</span>
        </span>
      )}
      {cliente.telefone && (
        <span className="clientes__meio">
          <Icone nome="telefone" />
          <span className="clientes__meio-texto">{cliente.telefone}</span>
        </span>
      )}
    </span>
  );
}

/** O valor do maior negócio aberto, à direita e tabular. */
function ValorEmJogo({ cliente }: { cliente: ClienteDaLista }): ReactNode {
  const micros = cliente.negocio?.valor_micros;
  if (micros == null) return <span className="clientes__nada">—</span>;

  return (
    <span
      className="clientes__valor"
      title={
        cliente.oportunidades_abertas > 1
          ? `A maior de ${cliente.oportunidades_abertas} oportunidades abertas`
          : undefined
      }
    >
      {dinheiro({ quantia_micros: micros, moeda: cliente.negocio?.moeda ?? 'BRL' })}
    </span>
  );
}

/**
 * Quem atende — e "Sem responsável" SE LÊ, porque é pendência.
 *
 * `semDisco` é para o celular. Na tabela o disco alinha a coluna inteira e o
 * olho varre os rostos; numa fileira de 226px ele custa 34px que o NOME
 * precisa, e o resultado era "Ana V…" ao lado de um disco escrito "AV" — o
 * mesmo dado duas vezes, nenhuma delas inteira.
 */
function Responsavel({
  cliente,
  semDisco = false,
}: {
  cliente: ClienteDaLista;
  semDisco?: boolean;
}): ReactNode {
  if (!cliente.responsavel) {
    return (
      <span className="clientes__sem-dono">
        {!semDisco && (
          <span className="clientes__sem-dono-disco" aria-hidden="true">
            <Icone nome="pessoa" />
          </span>
        )}
        Sem responsável
      </span>
    );
  }

  const nome = cliente.responsavel.nome ?? 'Atendente sem nome';
  return (
    <span className="clientes__responsavel">
      {!semDisco && <Avatar nome={nome} semente={cliente.responsavel.id_externo} tamanho={26} />}
      <span className="clientes__responsavel-nome">{nome}</span>
    </span>
  );
}

/** A célula do cliente: avatar, nome e contato como uma unidade. */
function Quem({ cliente, aoAbrir }: { cliente: ClienteDaLista; aoAbrir: () => void }): ReactNode {
  return (
    <span className="clientes__quem">
      <Avatar nome={cliente.nome_exibicao} semente={cliente.id} tamanho={32} />
      <span>
        {/*
          O nome é o controle de teclado da linha: o clique na linha inteira é
          atalho de mouse, e sem um botão aqui o Tab não chegaria à ficha.
        */}
        <button
          type="button"
          className="clientes__nome clientes__abrir"
          aria-label={`Abrir a ficha de ${cliente.nome_exibicao ?? 'contato sem nome'}`}
          onClick={aoAbrir}
        >
          {cliente.nome_exibicao ?? 'Contato sem nome'}
        </button>
        {/*
          Telefone e e-mail já vêm mascarados do servidor, e a tela não tenta
          completar. Quem precisa do número abre a ficha.
        */}
        {/* As etiquetas embaixo do nome: são o assunto de quem é a pessoa. */}
        {cliente.etiquetas.length > 0 && (
          <span className="clientes__etiquetas">
            <Etiquetas titulos={cliente.etiquetas} limite={2} emLinha />
          </span>
        )}
      </span>
    </span>
  );
}

export function celulasDe(cliente: ClienteDaLista, acoes: AcoesDaLinha): Record<string, ReactNode> {
  return {
    id: cliente.id,
    nome: cliente.nome_exibicao ?? 'contato sem nome',
    cliente: <Quem cliente={cliente} aoAbrir={() => acoes.abrirFicha(cliente)} />,
    meios: <Contato cliente={cliente} />,
    responsavel: <Responsavel cliente={cliente} />,
    etapa: <EtapaDoNegocio cliente={cliente} />,
    contato: <UltimoContatoComCanal cliente={cliente} />,
    acoes: <Controles cliente={cliente} acoes={acoes} />,
  };
}

/**
 * A linha do celular — a mesma informação, na ordem em que ela cabe.
 *
 * `controles` mantém as três trilhas: sem ele, os dois alvos de 44px descem
 * para baixo do nome e a linha vai de ~60px para 132px, quatro clientes numa
 * tela onde a variante densa promete doze.
 */
export function LinhaDeCliente({
  cliente,
  acoes,
}: {
  cliente: ClienteDaLista;
  acoes: AcoesDaLinha;
}): ReactNode {
  return (
    <ListaLinha
      densa
      controles
      rotulo={`Abrir a ficha de ${cliente.nome_exibicao ?? 'contato sem nome'}`}
      aoAbrir={() => acoes.abrirFicha(cliente)}
      inicio={<Avatar nome={cliente.nome_exibicao} semente={cliente.id} tamanho={32} />}
      nome={<span className="clientes__nome">{cliente.nome_exibicao ?? 'Contato sem nome'}</span>}
      previa={
        <span className="clientes__contato">
          {cliente.telefone ?? cliente.email ?? 'sem contato'}
        </span>
      }
      /*
       * Três itens na terceira linha, e não quatro.
       *
       * A ETAPA fica de fora aqui. Ela virou coluna justamente porque precisa
       * de um cabeçalho para significar alguma coisa: solta numa fileira de
       * celular, "Reunião" ao lado de um valor e de uma data é uma palavra sem
       * pergunta. Em 226px de trilha, os quatro juntos quebravam em duas
       * fileiras e a linha passava de 86px — e o que quebrava primeiro era o
       * "Sem responsável", que é o item que mais precisa ser lido.
       */
      meta={
        <>
          <Responsavel cliente={cliente} semDisco />
          <ValorEmJogo cliente={cliente} />
          <UltimoContato cliente={cliente} />
        </>
      }
      acoes={<Controles cliente={cliente} acoes={acoes} />}
    />
  );
}
